/**
 * RapportView.tsx — printbaar compliance-rapport
 *
 * Rendert het post-event rapport als een gestructureerde, printbare pagina.
 * Op scherm: donker thema met actie-balk (afdrukken + terug).
 * Afdrukken: window.print() → browser slaat op als PDF via print.css.
 *
 * Props:
 *   incidents      — IncidentEntry[] uit Firestore (mag leeg zijn voor preview)
 *   checklistState — ChecklistEntry[] uit Firestore "checklist"-collectie
 *   onClose        — callback voor de terug-knop
 */

import "../styles/print.css";
import { buildReport, INSTROOM_CAP } from "../lib/report";
import type { ReportIncidentEntry } from "../lib/report";
import type { ChecklistEntry } from "../lib/types";

interface Props {
  incidents: ReportIncidentEntry[];
  checklistState: ChecklistEntry[];
  onClose: () => void;
}

// ── Datum/tijd helpers (nl-NL, Amsterdam) ────────────────────────────────────

function fmtTime(ms: number): string {
  return new Date(ms).toLocaleTimeString("nl-NL", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Amsterdam",
  });
}

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString("nl-NL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Amsterdam",
  });
}

function fmtTimeMs(ms: number | null): string {
  if (ms === null) return "—";
  return fmtTime(ms);
}

// ── Severity-label helper ────────────────────────────────────────────────────

type Severity = "info" | "let-op" | "actie" | "alarm";

function severityLabel(s: Severity): string {
  const map: Record<Severity, string> = {
    info: "Info",
    "let-op": "Let op",
    actie: "Actie",
    alarm: "Alarm",
  };
  return map[s];
}

function severityClass(s: Severity): string {
  const map: Record<Severity, string> = {
    info: "sev-info",
    "let-op": "sev-letop",
    actie: "sev-actie",
    alarm: "sev-alarm",
  };
  return map[s];
}

// ── Component ────────────────────────────────────────────────────────────────

export function RapportView({ incidents, checklistState, onClose }: Props) {
  const r = buildReport(incidents, checklistState);
  const { header, samenvatting, instroom, meldingen, eccVoortgang, bommeldingen } = r;

  return (
    <div className="rapport-shell">

      {/* ── Actie-balk (verborgen bij afdrukken) ── */}
      <div className="rapport-actionbar no-print" role="toolbar" aria-label="Rapport acties">
        <div className="rapport-actionbar-left">
          <button className="rapport-btn-back" onClick={onClose} type="button">
            ← Terug
          </button>
          <span className="rapport-actionbar-title">
            Compliance-rapport — {header.event}
          </span>
        </div>
        <button
          className="rapport-btn-print"
          type="button"
          onClick={() => window.print()}
        >
          Afdrukken / opslaan als PDF
        </button>
      </div>

      {/* ── Rapport-inhoud ── */}
      <div className="rapport-wrap">

        {/* ═══ 1. Kop ═══════════════════════════════════════════════════════ */}
        {/* ── Print-kop (alleen zichtbaar bij afdrukken — Prepared A4-vormtaal) ── */}
        <header className="print-only rapport-printhead">
          <div className="rapport-printhead-top">
            <img src="/prepared-logo.png" alt="Prepared" className="rapport-printlogo" />
            <div className="rapport-printhead-right">
              <div className="rapport-print-eyebrow">Operationeel rapport · Command Post</div>
              <div className="rapport-print-doctitle">{header.event}</div>
              <div className="rapport-print-gen">Gegenereerd {fmtDateTime(header.generatedAt)}</div>
            </div>
          </div>
          <div className="rapport-print-metagrid">
            <div><span>Datum</span>{header.datum}</div>
            <div><span>Locatie</span>{header.locatie}</div>
            <div><span>Opdrachtgever</span>{header.opdrachtgever}</div>
            <div><span>CP-team</span>{header.cpTeam.join(" · ")}</div>
          </div>
        </header>

        <header className="rapport-header no-print">
          <div className="rapport-header-top">
            <div>
              <h1 className="rapport-event-title">{header.event}</h1>
              <p className="rapport-event-subtitle">
                Command Post — Operationeel Rapport
              </p>
            </div>
            <div style={{ textAlign: "right" }}>
              <div className="rapport-meta-label">Gegenereerd op</div>
              <div className="rapport-meta-value" style={{ fontSize: "0.8125rem" }}>
                {fmtDateTime(header.generatedAt)}
              </div>
            </div>
          </div>

          <div className="rapport-meta-grid">
            <div>
              <div className="rapport-meta-label">Datum</div>
              <div className="rapport-meta-value">{header.datum}</div>
            </div>
            <div>
              <div className="rapport-meta-label">Locatie</div>
              <div className="rapport-meta-value">{header.locatie}</div>
            </div>
            <div>
              <div className="rapport-meta-label">Opdrachtgever</div>
              <div className="rapport-meta-value">{header.opdrachtgever}</div>
            </div>
            <div>
              <div className="rapport-meta-label">CP-team</div>
              <div className="rapport-meta-value">
                {header.cpTeam.map((p, i) => (
                  <div key={i}>{p}</div>
                ))}
              </div>
            </div>
          </div>
        </header>

        {/* ═══ 2. Samenvatting ══════════════════════════════════════════════ */}
        <section className="rapport-section" aria-labelledby="h-samenvatting">
          <h2 className="rapport-section-title" id="h-samenvatting">
            Samenvatting
          </h2>
          <div className="rapport-summary-grid">
            <div className="rapport-stat-card">
              <div className="rapport-stat-label">Totaal meldingen</div>
              <div className="rapport-stat-value">{samenvatting.totaal}</div>
            </div>
            <div className="rapport-stat-card">
              <div className="rapport-stat-label">Open</div>
              <div className="rapport-stat-value">{samenvatting.open}</div>
            </div>
            <div className="rapport-stat-card">
              <div className="rapport-stat-label">Afgehandeld</div>
              <div className="rapport-stat-value">{samenvatting.afgehandeld}</div>
            </div>
            <div className="rapport-stat-card">
              <div className="rapport-stat-label">Alarmfase</div>
              <div className="rapport-stat-value">{samenvatting.perSeverity.alarm}</div>
            </div>
          </div>

          {/* Tijdvenster + verdeling */}
          <div className="rapport-meta-grid" style={{ marginTop: "0.875rem" }}>
            <div>
              <div className="rapport-meta-label">Eerste melding</div>
              <div className="rapport-meta-value">
                {fmtTimeMs(samenvatting.eerstemelding)}
              </div>
            </div>
            <div>
              <div className="rapport-meta-label">Laatste melding</div>
              <div className="rapport-meta-value">
                {fmtTimeMs(samenvatting.laatste)}
              </div>
            </div>
            <div>
              <div className="rapport-meta-label">Let-op / Actie</div>
              <div className="rapport-meta-value">
                {samenvatting.perSeverity["let-op"]} / {samenvatting.perSeverity.actie}
              </div>
            </div>
            <div>
              <div className="rapport-meta-label">Info-meldingen</div>
              <div className="rapport-meta-value">
                {samenvatting.perSeverity.info}
              </div>
            </div>
          </div>
        </section>

        {/* ═══ 3. Instroom-reeks (alleen bij aanwezige data) ═══════════════ */}
        {instroom.length > 0 && (
          <section className="rapport-section" aria-labelledby="h-instroom">
            <h2 className="rapport-section-title" id="h-instroom">
              Instroom-reeks (t.o.v. capaciteit {INSTROOM_CAP.toLocaleString("nl-NL")})
            </h2>
            <div className="rapport-table-wrap">
              <table className="rapport-table" aria-label="Instroom-reeks">
                <colgroup>
                  <col className="col-time" />
                  <col className="col-pct" />
                  <col />
                </colgroup>
                <thead>
                  <tr>
                    <th>Tijd</th>
                    <th>%</th>
                    <th>Melding</th>
                  </tr>
                </thead>
                <tbody>
                  {instroom.map((row, i) => (
                    <tr key={i}>
                      <td style={{ fontVariantNumeric: "tabular-nums" }}>
                        {fmtTime(row.time)}
                      </td>
                      <td style={{ fontVariantNumeric: "tabular-nums" }}>
                        {row.percentage !== null ? `${row.percentage}%` : "—"}
                      </td>
                      <td>{row.title}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* ═══ 4. Volledige meldingen-tabel ════════════════════════════════ */}
        <section className="rapport-section" aria-labelledby="h-meldingen">
          <h2 className="rapport-section-title" id="h-meldingen">
            Meldingen — chronologisch overzicht
          </h2>
          {meldingen.length === 0 ? (
            <p className="rapport-empty">
              Geen meldingen geregistreerd tijdens dit evenement.
            </p>
          ) : (
            <div className="rapport-table-wrap">
              <table className="rapport-table" aria-label="Meldingen">
                <colgroup>
                  <col className="col-time" />
                  <col className="col-sev" />
                  <col />
                  <col className="col-loc" />
                  <col className="col-by" />
                  <col className="col-stat" />
                </colgroup>
                <thead>
                  <tr>
                    <th>Tijd</th>
                    <th>Ernst</th>
                    <th>Melding / toelichting</th>
                    <th>Locatie</th>
                    <th>Melder</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {meldingen.map((row, i) => (
                    <tr key={i}>
                      <td style={{ fontVariantNumeric: "tabular-nums" }}>
                        {fmtTime(row.time)}
                      </td>
                      <td>
                        <span className={`sev-badge ${severityClass(row.severity)}`}>
                          {severityLabel(row.severity)}
                        </span>
                      </td>
                      <td>
                        <div>{row.title}</div>
                        {row.body && (
                          <div className="rapport-body-text">{row.body}</div>
                        )}
                      </td>
                      <td>{row.location}</td>
                      <td>{row.by}</td>
                      <td>
                        {row.resolved ? (
                          <span className="stat-resolved">Afgehandeld</span>
                        ) : (
                          <span className="stat-open">Open</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* ═══ 5. ECC-voortgang ════════════════════════════════════════════ */}
        <section className="rapport-section" aria-labelledby="h-ecc">
          <h2 className="rapport-section-title" id="h-ecc">
            ECC-overlegmomenten
          </h2>
          <div className="rapport-table-wrap">
            <table className="rapport-table" aria-label="ECC overleg">
              <colgroup>
                <col className="col-ecc-time" />
                <col className="col-ecc-check" />
                <col />
                <col className="col-ecc-by" />
                <col className="col-ecc-at" />
              </colgroup>
              <thead>
                <tr>
                  <th>Tijd</th>
                  <th>Afgevink</th>
                  <th>Overlegmoment</th>
                  <th>Door</th>
                  <th>Om</th>
                </tr>
              </thead>
              <tbody>
                {eccVoortgang.map((row) => (
                  <tr
                    key={row.id}
                    className={row.checked ? "" : "ecc-row-open"}
                  >
                    <td style={{ fontVariantNumeric: "tabular-nums" }}>
                      {row.tijd_van ?? "NTB"}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {row.checked ? (
                        <span className="ecc-check-icon" aria-label="Afgevink">✓</span>
                      ) : (
                        <span aria-label="Openstaand">—</span>
                      )}
                    </td>
                    <td className={row.checked ? "ecc-checked" : ""}>
                      <div>{row.wat}</div>
                      {row.note && (
                        <div className="rapport-body-text">{row.note}</div>
                      )}
                    </td>
                    <td className={row.checked ? "" : "ecc-checked"}>
                      {row.by}
                    </td>
                    <td style={{ fontVariantNumeric: "tabular-nums" }}>
                      {row.at !== null ? fmtTime(row.at) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* ═══ 6. Bommeldingen (conditioneel hoofdstuk) ════════════════════ */}
        {bommeldingen.length > 0 && (
          <section className="rapport-section" aria-labelledby="h-bom">
            <h2 className="rapport-section-title" id="h-bom">
              Bommeldingen
            </h2>
            <p style={{ fontSize: "0.8125rem", marginBottom: "0.875rem" }}
               className="rapport-body-text"
            >
              Onderstaande meldingen zijn afzonderlijk geregistreerd als bommelding
              en vereisen verplichte rapportage aan de opdrachtgever en bevoegde instanties.
            </p>
            {bommeldingen.map((b, i) => (
              <div className="rapport-bomb-block" key={i}>
                <div className="rapport-bomb-title">
                  {fmtTime(b.time)} — {b.title}
                </div>
                <div className="rapport-bomb-meta">
                  Locatie: {b.location} &nbsp;·&nbsp; Melder: {b.by} &nbsp;·&nbsp;{" "}
                  Status: {b.resolved ? "Afgehandeld" : "Open"}
                </div>
                {b.body && (
                  <p style={{ marginTop: "0.5rem", fontSize: "0.8125rem" }}>{b.body}</p>
                )}
              </div>
            ))}
          </section>
        )}

        {/* ═══ 7. Handtekening-blok + voettekst ═══════════════════════════ */}
        <div className="rapport-sig-grid">
          <div>
            <div className="rapport-sig-label">CP-operator</div>
            <div className="rapport-sig-line" />
            <div className="rapport-sig-sub">Naam, handtekening &amp; datum</div>
          </div>
          <div>
            <div className="rapport-sig-label">Opdrachtgever</div>
            <div className="rapport-sig-line" />
            <div className="rapport-sig-sub">Naam, handtekening &amp; datum</div>
          </div>
        </div>

        <footer className="rapport-footer">
          Opgesteld door Prepared (KvK 60817356) —{" "}
          {fmtDateTime(header.generatedAt)}
        </footer>

      </div>{/* /rapport-wrap */}
    </div>/* /rapport-shell */
  );
}
